import { GameDocument } from '../../../types/game-document';
import {
  EntityEditor,
  ResourceEntity,
  TaskEntity
} from '../../../types/game-document/';
import { uuid } from '../../../types/common-helper';
import { GetNextAssetNameAsync, MergeAssets } from './index';
import {
  CopyResourceAsync,
  DeleteResourceAsync,
  GetResourceKeys,
  SaveResourceAsync
} from '../resources';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { AlgorithmControlStructure, AlgorithmStep } from '../../../types/algorithm';
import { randomNumberInRange } from '../../number';
import { AddActionToTask } from '../settings';

/**
 * Adds a new Task to the Game document.
 * @param gameDocument - The Game Document to modify
 * @param name - The Name of the new Task
 * @param description - The Description for the new Task
 * @returns The updated Game Document
 */
export const AddTaskAsync = async (
  gameDocument: GameDocument,
  name: string,
  description: string,
  titleResId: string,
  imageResId: string
) => {
  let tasks = gameDocument.assets.tasks ?? [];
  let taskName = await GetNextAssetNameAsync(tasks, name);
  tasks.push({
    id: uuid(),
    name: taskName,
    description,
    titleResId,
    imageResId
  });
  return MergeAssets(gameDocument, tasks, 'tasks');
};

/**
 * Add task.
 * @param gameDocument - The Game Document to modify
 * @param tasks - The new tasks
 * @return The updated Game Document
 */
export const AddTaskEntityAsync = async (
  gameDocument: GameDocument,
  task: TaskEntity
) => {
  let tasks = gameDocument.assets.tasks ?? [];
  let taskName = await GetNextAssetNameAsync(tasks, task?.name);
  task.name = taskName;
  tasks.push(task);
  return MergeAssets(gameDocument, tasks, 'tasks');
};

/**
 * Deletes the identified Task from the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param taskId - The ID of the Task to delete
 * @returns The updated Game Document
 */
export const DeleteTaskAsync = async (
  gameDocument: GameDocument,
  taskId: string
) => {
  const tasks = gameDocument.assets.tasks ?? [];
  const taskIndex = tasks.findIndex((i) => i.id === taskId)!;

  if (taskIndex !== -1) {
    const defaultTaskAvailableIconResId = gameDocument.settings.designer?.defaultTaskAvailableIconResId;
    const defaultTaskCompletedIconResId = gameDocument.settings.designer?.defaultTaskCompletedIconResId;

    // remove title, available icon, and completed icon task's resource
    for (const res of GetResourceKeys(tasks[taskIndex])) {
      const resId = tasks[taskIndex][res] as string;
      if (
        resId &&
        resId !== defaultTaskAvailableIconResId &&
        resId !== defaultTaskCompletedIconResId
      ) {
        await DeleteResourceAsync(gameDocument, resId);
      }
    }

    tasks.splice(taskIndex, 1);
  }
  return MergeAssets(gameDocument, tasks, 'tasks');
};

/**
 * Remove boundary from task
 * @param gameDocument
 * @param taskId
 * @returns
 */
export const RemoveTaskBoundaryAsync = async (
  gameDocument: GameDocument,
  taskId: string
) => {
  let tasks = gameDocument.assets.tasks ?? [];
  let taskIndex = tasks.findIndex((i) => i.id === taskId)!;

  if (taskIndex > -1) {
    delete tasks[taskIndex].boundary;
    return MergeAssets(gameDocument, tasks, 'tasks');
  }
  return gameDocument;
};

/**
 * Updates the identified Task in the Game Document.
 * @param gameDocument - The Game Document to modify
 * @param taskId - The ID of the Task to update
 * @param task - The updated Task
 * @param renameDuplicate - Auto rename 'name' key if duplicate
 * @constructor
 */
export const UpdateTaskAsync = async (
  gameDocument: GameDocument,
  taskId: string,
  task: TaskEntity,
  renameDuplicate: boolean = true
) => {
  let tasks = gameDocument.assets.tasks ?? [];
  let taskIndex = tasks.findIndex((i) => i.id === taskId)!;
  if (renameDuplicate)
    task.name = await GetNextAssetNameAsync(tasks, task.name, task.id);
  tasks[taskIndex] = task;
  return MergeAssets(gameDocument, tasks, 'tasks');
};

/**
 * Create a copy of the Task in the Game document.
 * @param gameDocument - The Game Document to modify
 * @param taskId - The ID of the Task to copy
 * @returns The updated Game Document
 */
export const CopyTaskAsync = async (
  gameDocument: GameDocument,
  taskId: string,
  copiedTaskId: string,
  resetBoundary: boolean = false
) => {
  let tasks = gameDocument.assets.tasks ?? [];
  let taskIndex = tasks.findIndex((i) => i.id === taskId)!;

  if (taskIndex !== -1) {
    const defaultTaskAvailableIconResId = gameDocument.settings.designer?.defaultTaskAvailableIconResId;
    const defaultTaskCompletedIconResId = gameDocument.settings.designer?.defaultTaskCompletedIconResId;

    let taskCopy: TaskEntity = cloneDeep({
      ...tasks[taskIndex],
      titleResId: uuid(),
      imageResId: tasks[taskIndex].imageResId === defaultTaskAvailableIconResId ? defaultTaskAvailableIconResId : uuid(),
      completeImageResId: tasks[taskIndex].completeImageResId === defaultTaskCompletedIconResId ? defaultTaskCompletedIconResId : uuid()
    });

    if (taskCopy.boundary) {
      taskCopy.boundary.id = uuid();
      if (taskCopy.boundary.geometry) {
        taskCopy.boundary.geometry.coordinates[0] += randomNumberInRange(1, 5);
        taskCopy.boundary.geometry.coordinates[1] += randomNumberInRange(1, 5);
      }
    }

    // Recursively regenerate all IDs in steps (including nested control structures)
    const regenerateStepIds = (
      steps?: Array<AlgorithmStep | AlgorithmControlStructure>
    ) => {
      steps?.forEach((item) => {
        item.id = uuid();
        let algorithmStructure = item as AlgorithmControlStructure;
        if (algorithmStructure.ifSteps || algorithmStructure.elseSteps) {
          algorithmStructure?.ifCompoundStatements?.forEach((compound) => {
            compound.id = uuid();
          });
          algorithmStructure?.elseCompoundStatements?.forEach((compound) => {
            compound.id = uuid();
          });
          regenerateStepIds(algorithmStructure.ifSteps);
          regenerateStepIds(algorithmStructure.elseSteps);
        }
      });
    };

    regenerateStepIds(taskCopy?.events?.onOpenTask?.steps);
    regenerateStepIds(taskCopy?.events?.onCompleteTask?.steps);
    regenerateStepIds(taskCopy?.events?.onCloseTask?.steps);
    regenerateStepIds(taskCopy?.events?.onProximity?.steps);

    await CopyResourceAsync(
      gameDocument,
      tasks[taskIndex].titleResId!,
      taskCopy.titleResId,
      false
    );

    if (taskCopy.imageResId !== defaultTaskAvailableIconResId) {
      await CopyResourceAsync(
        gameDocument,
        tasks[taskIndex].imageResId!,
        taskCopy.imageResId
      );
    }

    if (taskCopy.completeImageResId !== defaultTaskCompletedIconResId) {
      await CopyResourceAsync(
        gameDocument,
        tasks[taskIndex].completeImageResId!,
        taskCopy.completeImageResId
      );
    }

    taskCopy.id = copiedTaskId;
    taskCopy.name += '-copy';
    taskCopy.name = await GetNextAssetNameAsync(
      tasks,
      taskCopy.name,
      taskCopy.id
    );
    if (resetBoundary) delete taskCopy.boundary;
    tasks.push(taskCopy);
  }
  return MergeAssets(gameDocument, tasks, 'tasks');
};
/**
 * Get all tasks from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasks = (gameDocument: GameDocument | undefined) => {
  return gameDocument?.assets?.tasks ?? [];
};

/**
 * Get all tasks from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksHasBoundary = (gameDocument: GameDocument | undefined) => {
  return (
    gameDocument?.assets?.tasks?.filter((x) => x.boundary !== undefined) ?? []
  );
};

/**
 * Get all tasks that has no boundary from game document local storage that has.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksHasNoBoundary = (
  gameDocument: GameDocument | undefined
) => {
  return (
    gameDocument?.assets?.tasks?.filter((x) => x.boundary === undefined) ?? []
  );
};

/**
 * Get tasks by Zone Id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksByZoneId = (
  zoneId: string,
  gameDocument: GameDocument | undefined
) => {
  let tasks: TaskEntity[] = [];

  if (gameDocument) {
    let zone = gameDocument?.assets?.zones?.find((x) => x.id === zoneId);

    const taskIds = zone?.tasks?.map(function (e) {
      return e.taskAssId;
    });

    if (taskIds) {
      return GetTasksByIds(gameDocument, taskIds);
    }
  }

  return tasks;
};

/**
 * Get tasks by Ids from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksByIds = (
  gameDocument: GameDocument | undefined,
  ids: string[]
) => {
  return (
    gameDocument?.assets?.tasks?.filter((x) => ids.indexOf(x.id) !== -1) ?? []
  );
};

/**
 * Get tasks by Id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @param id - The task ID
 * @constructor
 */
export const GetTaskById = (gameDocument: GameDocument, id: string) => {
  return gameDocument.assets?.tasks?.find((x) => x.id === id);
};

export const SaveEntityEditorAsync = async (
  gameDocument: GameDocument,
  editorEntity: EntityEditor<TaskEntity>,
  resourceEntity: EntityEditor<ResourceEntity>[]
) => {
  let entity = editorEntity.entity;
  let newGameDocument: GameDocument = cloneDeep(gameDocument);

  const defaultTaskAvailableIconResId = newGameDocument.settings.designer?.defaultTaskAvailableIconResId || '';
  const defaultTaskCompletedIconResId = newGameDocument.settings.designer?.defaultTaskCompletedIconResId || '';

  if (!editorEntity.isNew) {
    const existingTask = gameDocument.assets.tasks?.find((t) => t.id === entity.id);
    if (existingTask) {
      const prevImageResId = existingTask.imageResId;
      if (
        prevImageResId &&
        prevImageResId !== defaultTaskAvailableIconResId &&
        entity.imageResId !== prevImageResId
      ) {
        newGameDocument = await DeleteResourceAsync(newGameDocument, prevImageResId);
      }

      const prevCompleteImageResId = existingTask.completeImageResId;
      if (
        prevCompleteImageResId &&
        prevCompleteImageResId !== defaultTaskCompletedIconResId &&
        entity.completeImageResId !== prevCompleteImageResId
      ) {
        newGameDocument = await DeleteResourceAsync(newGameDocument, prevCompleteImageResId);
      }
    }
  }

  const saveTitleResource = SaveResourceAsync(
    newGameDocument,
    resourceEntity?.find((x) => x.entity.id === entity.titleResId)?.entity!
  );

  if (!entity.imageResId) {
    entity.imageResId = defaultTaskAvailableIconResId;
  }
  if (!entity.completeImageResId) {
    entity.completeImageResId = defaultTaskCompletedIconResId;
  }

  let saveImageResource: Promise<GameDocument | undefined> = Promise.resolve(newGameDocument);
  if (entity.imageResId && entity.imageResId !== defaultTaskAvailableIconResId) {
    const imageResource = resourceEntity?.find(
      (x) => x.entity.id === entity.imageResId
    )?.entity;
    if (imageResource) {
      saveImageResource = SaveResourceAsync(newGameDocument, imageResource);
    }
  }

  let saveCompleteImageResource: Promise<GameDocument | undefined> = Promise.resolve(newGameDocument);
  if (entity.completeImageResId && entity.completeImageResId !== defaultTaskCompletedIconResId) {
    const completeImageResource = resourceEntity?.find(
      (x) => x.entity.id === entity.completeImageResId
    )?.entity;
    if (completeImageResource) {
      saveCompleteImageResource = SaveResourceAsync(
        newGameDocument,
        completeImageResource
      );
    }
  }

  const [titleResponse, imageResponse, completeImageResponse] =
    await Promise.all([
      saveTitleResource,
      saveImageResource,
      saveCompleteImageResource
    ]);

  if (editorEntity.isNew) {
    editorEntity.entity.id = uuid();
    // Only apply default title visibility if not already set (e.g., from JSON import)
    if (editorEntity.entity.isTitleVisible === undefined) {
      editorEntity.entity.isTitleVisible =
        newGameDocument.settings.designer.defaultTaskTitleVisibility;
    }
    await AddActionToTask(newGameDocument, editorEntity.entity);
    newGameDocument = merge(
      await AddTaskEntityAsync(newGameDocument, editorEntity.entity)
    );
  } else {
    newGameDocument = merge(
      await UpdateTaskAsync(
        newGameDocument,
        editorEntity.entity.id,
        editorEntity.entity
      )
    );
  }

  newGameDocument = merge(newGameDocument, titleResponse);
  if (imageResponse) {
    newGameDocument = merge(newGameDocument, imageResponse);
  }
  if (completeImageResponse) {
    newGameDocument = merge(newGameDocument, completeImageResponse);
  }

  return newGameDocument;
};
